import { useState } from "react";
import { Reveal } from "@/components/Reveal";

const D = {
  wo: ["WideOrbit", "Stays exactly as it is.", ["Nightly sync creates monthly Billing records", "Existing workflows sort them into the 8 pacing categories", "Only change: billing codes aligned to the same 4-digit account codes as budgets"]],
  tpl: ["Budget templates", "One standard template per market, imported once a year.", ["Owner \u00d7 station \u00d7 account code \u00d7 month", "We send back a corrected template first", "Each market gets an exceptions report if totals don't tie out"]],
  li: ["Deal line items", "Back in use, so pipeline can be spread by month.", ["Each product carries station and account code", "Start date, end date, amount and billing frequency drive the split", "AEs get short training on the new deal entry"]],
  bill: ["Billing (existing)", "Booked revenue, one record per order line per month.", ["Cash only for pacing; trade excluded", "\u201cWO Is New Business\u201d flag feeds New Business reporting", "No changes to the integration itself"]],
  mb: ["Monthly Budget (new)", "Replaces the 382 Goals with one clean structure.", ["One record per owner, station, account code and month", "Budget owner is its own field, so vacant seats and market-level Paid Programming still count", "Also the record the roll-up writes pacing numbers onto"]],
  fr: ["Forecasted Revenue (new)", "Custom code splits each line item into monthly records.", ["Rebuilt when a deal changes: new records created first, then old ones removed", "Removed when the deal closes, so WideOrbit takes over and nothing is counted twice", "A nightly reconciliation catches anything the triggers miss"]],
  ru: ["Pacing roll-up (our addition)", "A nightly job after the WideOrbit sync.", ["Sums billing and forecast records with the same owner, station, account code and month", "Applies the stage weightings from one admin-editable settings record", "Writes booked, pending, projected, variance and new business onto each budget record", "Creates a $0 \u201cunbudgeted\u201d row when revenue lands where nothing was budgeted"]],
  ae: ["AE dashboard", "Each AE sees their own pacing.", ["Budget, booked, pending and projected by month", "By category group and category", "Filtered to the logged-in AE"]],
  mk: ["Market dashboard", "GMs see their market, and drill into it.", ["Totals for the market, including vacant seats and Paid Programming", "Drill into AEs, stations and categories with filters", "Visibility set up through HubSpot Teams per market"]],
  co: ["Corporate dashboard", "Company-wide view.", ["By market, category group, category and AE", "New Business by month", "Variance to budget across all 12 markets"]],
};

const N = {
  wo: { x: 20, y: 52, t: "WideOrbit", s: "Billing & traffic system" },
  tpl: { x: 20, y: 160, t: "Budget templates", s: "12 markets, once a year", badge: null },
  li: { x: 20, y: 268, t: "Deal line items", s: "Entered by AEs" },
  bill: { x: 248, y: 52, t: "Billing", s: "Existing \u00b7 nightly sync" },
  mb: { x: 248, y: 160, t: "Monthly Budget", s: "Replaces 382 Goals", badge: "NEW" },
  fr: { x: 248, y: 268, t: "Forecasted Revenue", s: "One record per line item per month", badge: "NEW" },
  ru: { x: 498, y: 140, t: "Pacing roll-up", s: ["Writes booked, pending,", "projected and variance onto", "each budget record"], badge: "OUR ADDITION", key: true, h: 100 },
  ae: { x: 738, y: 52, t: "AE dashboard", s: "My budget and pacing" },
  mk: { x: 738, y: 160, t: "Market dashboard", s: "GMs, drill to AE and station" },
  co: { x: 738, y: 268, t: "Corporate dashboard", s: "By market, category, AE" },
};

const COLS = [
  ["SOURCE", 20], ["HUBSPOT OBJECT", 250], ["NIGHTLY JOB", 500], ["WHO SEES IT", 740],
];

const EDGES = [
  "M196 82H244", "M196 190H244", "M196 298H244",
  "M444 82H470V170H494", "M444 298H470V210H494", "M444 190H494",
  "M694 190H712V82H734", "M694 190H734", "M694 190H712V298H734",
];

export default function Architecture() {
  const [sel, setSel] = useState("ru");
  const d = D[sel];

  return (
    <section id="how" className="mx-auto max-w-6xl px-4 sm:px-6 mt-14 scroll-mt-16" data-testid="section-how">
      <Reveal>
        <p className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-fog">How it works</p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-coal">Three sources feed one pacing record</h2>
        <p className="mt-2 text-[15px] text-fog">Click any box to see what it does.</p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-5 bg-white border border-hair rounded-md p-5" data-testid="how-panel">
          <div className="overflow-x-auto">
            <svg className="arch min-w-[700px] w-full" viewBox="0 0 960 380" role="img" aria-label="Architecture: three sources feed three objects, a nightly roll-up writes onto the budget record, dashboards read from it">
              <defs>
                <marker id="ah" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0 0L10 5L0 10z" fill="#5E666D" />
                </marker>
              </defs>
              {COLS.map(([label, x]) => (
                <text key={label} x={x} y="24" fontSize="10.5" letterSpacing="0.08em" fill="#5E666D" fontFamily="IBM Plex Mono, monospace">{label}</text>
              ))}
              {EDGES.map((dPath, i) => (
                <path key={i} d={dPath} fill="none" stroke="#5E666D" strokeWidth="1.25" markerEnd="url(#ah)" strokeDasharray={i === 5 ? "4 4" : undefined} />
              ))}
              {Object.entries(N).map(([k, n]) => {
                const selected = sel === k;
                return (
                  <g key={k} data-testid={`how-node-${k}`} onClick={() => setSel(k)} style={{ cursor: "pointer" }}>
                    <rect
                      x={n.x} y={n.y} width={k === "ru" ? 196 : 196} height={n.h || 60} rx="6"
                      fill={n.key ? "#EEF1F3" : "#FFFFFF"}
                      stroke={selected ? "#16181A" : n.key || n.badge ? "#16181A" : "#D6DBDF"}
                      strokeWidth={selected ? 2.5 : n.key || n.badge ? 1.5 : 1.25}
                    />
                    {n.badge && (
                      <text x={n.x + 14} y={n.y + 20} fontSize="9.5" letterSpacing="0.06em" fill="#16181A" fontFamily="IBM Plex Mono, monospace">{n.badge}</text>
                    )}
                    <text x={n.x + 14} y={n.y + (n.badge ? 34 : 26)} fontSize="13.5" fontWeight="600" fill="#16181A" fontFamily="Instrument Sans, sans-serif">{n.t}</text>
                    {(Array.isArray(n.s) ? n.s : [n.s]).map((line, li) => (
                      <text key={li} x={n.x + 14} y={n.y + (n.badge ? 51 + li * 16 : 45)} fontSize="11.5" fill="#5E666D" fontFamily="Instrument Sans, sans-serif">{line}</text>
                    ))}
                  </g>
                );
              })}
              <text x="452" y="356" fontSize="11.5" fill="#5E666D" fontFamily="Instrument Sans, sans-serif">Matched on budget owner + station + account code + month</text>
            </svg>
          </div>

          <div className="mt-4 border-l-[3px] border-coal pl-4" data-testid="how-detail-panel">
            <h3 className="font-display text-lg text-coal">{d[0]}</h3>
            <p className="mt-1 text-[14.5px] text-coal/85">{d[1]}</p>
            <ul className="mt-2 pl-5 list-disc space-y-1 text-[14px] text-fog">
              {d[2].map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
